import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CARRIERS, carriersForProduct } from "@/data/carriers";
import { CARRIER_LOGOS } from "@/data/carrierLogos";

/**
 * Phase 14.1 — "Available from" strip for product/service pages.
 *
 * Shows 2–4 carrier logos (clickable) that write policies for the given
 * product slug. If no carriers map to the slug, the component renders null
 * so empty strips don't pollute the page. Each logo falls back to a
 * typographic tile on image error (same pattern as CarrierStrip).
 */

const AvailableTile = ({ carrier }) => {
  const logo = CARRIER_LOGOS[carrier.slug];
  const [errored, setErrored] = useState(false);
  const showImg = logo && !errored;

  return (
    <Link
      to={`/carriers/${carrier.slug}`}
      data-testid={`available-from-${carrier.slug}`}
      aria-label={`See products we offer from ${carrier.name}`}
      className="group relative rounded-lg border border-lw-navy/10 bg-white hover:border-lw-gold/60 hover:shadow-[0_12px_28px_-20px_rgba(10,37,64,0.35)] transition-all duration-300 flex items-center justify-center px-4 py-4"
      style={{ minHeight: 72 }}
    >
      {showImg ? (
        <img
          src={logo.src}
          alt={`${carrier.name} logo`}
          loading="lazy"
          onError={() => setErrored(true)}
          className="max-w-[140px] max-h-[44px] w-auto h-auto object-contain transition-all duration-300"
          style={{ filter: "grayscale(100%)", opacity: 0.7 }}
          onMouseEnter={(e) => {
            e.currentTarget.style.filter = "none";
            e.currentTarget.style.opacity = "1";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.filter = "grayscale(100%)";
            e.currentTarget.style.opacity = "0.7";
          }}
        />
      ) : (
        <span className="font-serif-display text-sm text-lw-navy">
          {carrier.name}
        </span>
      )}
    </Link>
  );
};

/**
 * @param {Object} props
 * @param {string} props.productSlug - product slug (e.g. "iul-accumulation")
 * @param {string} [props.productName] - display name, for the heading
 * @param {number} [props.limit=4] - max tiles to show
 */
export const AvailableFromStrip = ({ productSlug, productName, limit = 4 }) => {
  const carriers = carriersForProduct(productSlug).slice(0, limit);
  if (carriers.length === 0) return null;

  return (
    <section
      id="available-from"
      data-testid={`available-from-strip-${productSlug}`}
      className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 mt-16"
    >
      <div className="rounded-2xl border border-lw-navy/10 bg-lw-ivory-warm/60 p-6 sm:p-8">
        <div className="flex items-baseline justify-between flex-wrap gap-2 mb-5">
          <div>
            <span className="lw-eyebrow">Carrier partners</span>
            <h3 className="mt-2 font-serif-display text-xl sm:text-2xl text-lw-navy leading-tight">
              Available from our carrier partners{productName ? ` for ${productName}` : ""}
            </h3>
          </div>
          <Link
            to="/carriers"
            className="text-xs font-semibold text-lw-navy underline-offset-4 hover:underline"
          >
            See all carriers
          </Link>
        </div>
        <ul className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {carriers.map((c) => (
            <li key={c.slug}>
              <AvailableTile carrier={c} />
            </li>
          ))}
        </ul>
        <p className="mt-5 text-[0.72rem] leading-relaxed text-lw-muted">
          Carrier availability varies by state and underwriting. This is not an
          exhaustive list. Lifespan Wealth Partners is an independent licensed
          insurance agency; the carriers above do not endorse or sponsor us.
        </p>
      </div>
    </section>
  );
};

export default AvailableFromStrip;
